import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  InputAdornment,
  Chip,
  Alert,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import UploadIcon from '@mui/icons-material/CloudUpload';
import AIIcon from '@mui/icons-material/AutoAwesome';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useAuth } from '../../context/AuthContext';
import { useShop } from '../../context/ShopContext';
import { categories, Product } from '../../data/mockMarketplace';
import { useProducts } from '../../context/ProductContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import { NotificationService } from '../../services/errorService';

const ManageProducts: React.FC = () => {
  const { user } = useAuth();
  const { activeShop, shops, setActiveShopId } = useShop();
  const navigate = useNavigate();
  const { products, addProduct, updateProduct, deleteProduct } = useProducts();

  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [uploading, setUpload] = useState(false);

  // Form state
  const [formData, setFormData] = useState<Partial<Product>>({
    name: '',
    category: '',
    price: 0,
    unit: '',
    stock: 0,
    description: '',
    image: '',
  });
  
  // Check if user has at least one shop OR has a legacy seller role
  const canManageProducts = ['farmer', 'seller', 'admin'].includes(user?.role || '') || (shops && shops.length > 0);

  if (!canManageProducts) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access Denied: You need to be a registered Seller or Farmer to manage products. 
          Please create a shop first if you haven't already.
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/dashboard/my-shops')} 
          sx={{ mt: 2 }}
        >
          Go to My Shops
        </Button>
      </Box>
    );
  }

  // Shop selection prompt if no active shop
  if (!activeShop) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <Typography variant="h5" gutterBottom>Select a Shop to Manage Products</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          You need to select a shop before you can manage its inventory.
        </Typography>
        
        {shops.length === 0 ? (
          <Button variant="contained" onClick={() => navigate('/dashboard/my-shops')}>
            Create Your First Shop
          </Button>
        ) : (
          <Grid container spacing={2} sx={{ maxWidth: 600 }}>
            {shops.map(shop => (
              <Grid item xs={12} sm={6} key={shop.id}>
                <Card 
                  sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }} 
                  onClick={() => setActiveShopId(shop.id)}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">{shop.name}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
    (product as any).shop_id === activeShop.id
  );

  const handleOpenDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setFormData(product);
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        category: '',
        price: 0,
        unit: '',
        stock: 0,
        description: '',
        image: 'https://images.unsplash.com/photo-1551754655-cd27e38d2076?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', // Default image
        shop_id: activeShop.id, // Ensure new product is associated with current shop
      } as any);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'price' || name === 'stock' ? Number(value) : value,
    }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0 || !activeShop) return;
    const file = e.target.files[0];
    const fileName = `${activeShop.id}/products/${Date.now()}_${file.name}`;

    setUpload(true);
    try {
      const { data, error } = await supabase.storage
        .from('shop-assets')
        .upload(fileName, file);

      if (error) {
        NotificationService.error('Upload Failed', error.message);
        return;
      }

      const { data: { publicUrl } } = supabase.storage.from('shop-assets').getPublicUrl(fileName);
      setFormData((prev) => ({ ...prev, image: publicUrl }));
      NotificationService.success('Success', 'Image uploaded successfully!');
    } catch (err: any) {
      NotificationService.error('Error', err.message);
    } finally {
      setUpload(false);
    }
  };

  const handleSave = async () => {
    if (!activeShop) return;
    
    try {
      if (editingProduct) {
        // Update existing product
        await updateProduct(editingProduct.id, formData);
        NotificationService.success('Success', 'Product updated successfully');
      } else {
        // Add new product
        const newProductData = {
          ...formData,
          shop_id: activeShop.id,
          seller_id: user?.id,
          seller: {
            id: user?.id || '1',
            name: activeShop.name,
            location: 'Nairobi',
            rating: 5,
            verified: true,
            joinedDate: new Date().toISOString().split('T')[0],
            responseRate: '100%',
          },
          features: [],
          tags: [],
        } as any;
        
        await addProduct(newProductData);
        NotificationService.success('Success', 'Product added successfully');
      }
      handleCloseDialog();
    } catch (error: any) {
      NotificationService.error('Error saving product', error.message);
    }
  };

  const handleDelete = async (id: string | number) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(id);
        NotificationService.success('Success', 'Product deleted successfully');
      } catch (error: any) {
        NotificationService.error('Error deleting product', error.message);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h5" fontWeight="bold">
          Manage Products
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={() => setActiveShopId(null)}
          >
            Switch Shop
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Product
          </Button>
        </Stack>
      </Box>

      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Managing inventory for: <strong>{activeShop.name}</strong>
      </Typography>

      {/* AI Inventory Intelligence Panel */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: '16px', border: '1px solid', borderColor: '#3f51b5', bgcolor: 'rgba(63, 81, 181, 0.05)' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <AIIcon sx={{ color: '#3f51b5' }} />
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#1a237e' }}>AI Inventory Intelligence</Typography>
          <Chip label="Smart Alerts" size="small" sx={{ fontWeight: 'bold', bgcolor: '#3f51b5', color: 'white' }} />
        </Stack>
        
        <Grid container spacing={3}>
          {filteredProducts.filter(p => p.stock <= 10).length > 0 ? (
            filteredProducts.filter(p => p.stock <= 10).slice(0, 2).map(product => (
              <Grid item xs={12} md={6} key={`alert-${product.id}`}>
                <Card variant="outlined" sx={{ borderRadius: '12px', bgcolor: 'white', border: '1px solid', borderColor: '#ff9800' }}>
                  <CardContent sx={{ py: '12px !important' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ p: 1, bgcolor: '#fff3e0', borderRadius: '8px' }}>
                        <WarningIcon sx={{ color: '#ef6c00' }} />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#e65100' }}>Stock-out Risk: {product.name}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                          Current stock: {product.stock} {product.unit}. Estimated to run out in 3 days based on current demand.
                        </Typography>
                      </Box>
                      <Button size="small" variant="contained" sx={{ bgcolor: '#3f51b5', '&:hover': { bgcolor: '#303f9f' } }} onClick={() => handleOpenDialog(product)}>
                        Restock
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  All inventory levels are optimal. AI predicts no stock-outs for the next 7 days.
                </Typography>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" spacing={2} alignItems="center">
              <TrendingUpIcon sx={{ color: '#2e7d32' }} fontSize="small" />
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                <strong style={{ color: '#3f51b5' }}>Demand Insight:</strong> "Hybrid Seeds" are trending in your region. Consider increasing stock by 20% to capture an estimated <strong style={{ color: '#2e7d32' }}>KSh 15,000</strong> in additional revenue.
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ mb: 3, p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search products..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  <Box
                    component="img"
                    src={product.image}
                    alt={product.name}
                    sx={{ width: 50, height: 50, borderRadius: 1, objectFit: 'cover' }}
                  />
                </TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell>{product.category}</TableCell>
                <TableCell>KES {product.price}</TableCell>
                <TableCell>{product.stock} {product.unit}</TableCell>
                <TableCell>
                  <Chip
                    label={product.stock > 0 ? 'In Stock' : 'Out of Stock'}
                    color={product.stock > 0 ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(product)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(product.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredProducts.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">No products found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Product Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="name"
                label="Product Name"
                fullWidth
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="category"
                label="Category"
                select
                fullWidth
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                {categories.map((option) => (
                  <MenuItem key={option.id} value={option.name}>
                    {option.icon} {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="price"
                label="Price (KES)"
                type="number"
                fullWidth
                value={formData.price}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="unit"
                label="Unit (e.g., kg, piece)"
                fullWidth
                value={formData.unit}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="stock"
                label="Stock Quantity"
                type="number"
                fullWidth
                value={formData.stock}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                multiline
                rows={4}
                fullWidth
                value={formData.description}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {formData.image && (
                  <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Box
                      component="img"
                      src={formData.image}
                      alt="Preview"
                      sx={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 1 }}
                    />
                  </Paper>
                )}
                <TextField
                  name="image"
                  label="Image URL"
                  fullWidth
                  value={formData.image}
                  onChange={handleInputChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          component="label" 
                          edge="end"
                          disabled={uploading}
                        >
                          {uploading ? <CircularProgress size={24} /> : <PhotoCamera />}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleFileChange}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.name || !formData.price}>
            Save Product
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageProducts;
